import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Printer, Layers } from "lucide-react";
import { useShortlist } from "@/context/ShortlistContext";
import { Logo } from "./Logo";
import { fetcher, asList } from "@/lib/api";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { data } = useSWR("/categories", fetcher);
  const cats = asList(data);
  const { ids } = useShortlist();

  return (
    <header className="fixed top-0 inset-x-0 z-50 no-print">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-10 h-20 flex items-center justify-between backdrop-blur-xl bg-[#0D0D11]/60 border-b border-[#D4AF37]/10">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setOpen(false)}><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-8">
          {cats.slice(0, 6).map((c) => (
            <Link key={c.id} to={`/c/${c.slug}`} data-testid={`nav-cat-${c.slug}`}
              className="nav-link text-sm text-[#A09E97] hover:text-[#F5F3EF] transition-colors duration-300">
              {c.name}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/selection" data-testid="nav-selection-link" aria-label="Your selection"
            className="relative h-11 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors duration-300">
            <Layers className="w-4 h-4" /><span className="hidden xl:inline text-sm">Selection</span>
            {ids.length > 0 && <span data-testid="nav-selection-count" className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-[#D4AF37] text-[#0D0D11] text-[11px] font-medium">{ids.length}</span>}
          </Link>
          <Link to="/print" data-testid="nav-print-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-xs font-mono-j uppercase tracking-[0.2em] text-[#E5C158] hover:bg-[#D4AF37] hover:text-[#0D0D11] transition-colors duration-300">
            <Printer className="w-3.5 h-3.5" /> A4 Print
          </Link>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu"
            className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-[#D4AF37]/30 text-[#E5C158]">
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="lg:hidden bg-[#0D0D11]/95 backdrop-blur-xl border-b border-[#D4AF37]/15 px-5 py-8" data-testid="nav-mobile-menu">
            {cats.map((c, i) => (
              <Link key={c.id} to={`/c/${c.slug}`} onClick={() => setOpen(false)} data-testid={`nav-mobile-cat-${c.slug}`}
                className="flex items-baseline justify-between py-3 border-b border-[#D4AF37]/10">
                <span className="font-display text-3xl text-[#F5F3EF]">{c.name}</span>
                <span className="font-mono-j text-xs text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>
              </Link>
            ))}
            <Link to="/print" onClick={() => setOpen(false)} data-testid="nav-mobile-print"
              className="mt-6 inline-flex items-center gap-2 font-mono-j text-xs uppercase tracking-[0.2em] text-[#E5C158]">
              <Printer className="w-4 h-4" /> Printable A4 catalogue
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
